function stripQueryAndFragment(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const queryIndex = value.indexOf('?');
  const fragmentIndex = value.indexOf('#');
  const end = [queryIndex, fragmentIndex]
    .filter(index => index >= 0)
    .reduce((lowest, index) => Math.min(lowest, index), value.length);
  return value.slice(0, end);
}

function sanitizeUrlFields(data: unknown): unknown {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const sanitized = { ...(data as Record<string, unknown>) };
  [
    'url',
    'http.url',
    'http.target',
    'url.full',
    'url.original',
    'from',
    'to',
  ].forEach((key) => {
    if (key in sanitized) sanitized[key] = stripQueryAndFragment(sanitized[key]);
  });
  delete sanitized.query;
  delete sanitized.query_string;
  delete sanitized['http.query'];
  delete sanitized['http.fragment'];
  delete sanitized['url.query'];
  delete sanitized['url.fragment'];
  return sanitized;
}

// Replay's underlying rrweb Meta event records window.location.href before
// application callbacks can sanitize it. Keep Replay fully disabled so
// password-reset tokens and other URL credentials never enter recording data.
export const SENTRY_REPLAY_PRIVACY_OPTIONS = {
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
} as const;

export function sanitizeSentryBreadcrumb<T>(breadcrumb: T): T {
  if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;
  const value = breadcrumb as Record<string, unknown>;
  return {
    ...value,
    ...(value.data ? { data: sanitizeUrlFields(value.data) } : {}),
  } as T;
}

function sanitizeTraceOrSpan<T extends Record<string, unknown>>(value: T): T {
  return {
    ...value,
    ...(typeof value.description === 'string'
      ? { description: stripQueryAndFragment(value.description) }
      : {}),
    ...(value.data ? { data: sanitizeUrlFields(value.data) } : {}),
  };
}

/**
 * Keep user-entered filters and other URL query values out of frontend
 * observability. Route paths, methods, statuses, and timings remain available
 * for diagnosis.
 */
export function sanitizeSentryEvent<T>(event: T): T {
  if (!event || typeof event !== 'object') return event;
  const value = event as Record<string, any>;
  const sanitized: Record<string, any> = { ...value };

  if (value.request && typeof value.request === 'object') {
    const request = sanitizeUrlFields(value.request) as Record<string, unknown>;
    delete request.data;
    sanitized.request = request;
  }
  if (typeof value.transaction === 'string') {
    sanitized.transaction = stripQueryAndFragment(value.transaction);
  }
  if (Array.isArray(value.breadcrumbs)) {
    sanitized.breadcrumbs = value.breadcrumbs.map(sanitizeSentryBreadcrumb);
  }
  if (value.contexts?.trace && typeof value.contexts.trace === 'object') {
    sanitized.contexts = {
      ...value.contexts,
      trace: sanitizeTraceOrSpan(value.contexts.trace),
    };
  }
  if (Array.isArray(value.spans)) {
    sanitized.spans = value.spans.map(sanitizeTraceOrSpan);
  }

  return sanitized as T;
}
